import { getUrl } from '@buffer-mono/shared-utils/bibe'

import { IS_WITH_PROD, IS_WITH_STAGING } from '~publish/env'
import type { Image } from '~publish/legacy/composer/composer/entities/factories'

export const MAX_IMAGE_WORKSPACE_SOURCE_DIMENSION = 4_096

export type ImageWorkspaceSourceDescriptor = Readonly<{
  requestUrl: string
  preparationUrl: string
  canonicalUrl: string
  canonicalWidth: number | null
  canonicalHeight: number | null
  maxPreparedWidth: number
  maxPreparedHeight: number
  usesPreparationProxy: boolean
}>

type ImageWorkspaceSourceEnvironment = Readonly<{
  isWithProd: boolean
  isWithStaging: boolean
}>

const getImageWorkspacePreparationUrl = (
  canonicalUrl: string,
  environment: ImageWorkspaceSourceEnvironment,
): string => {
  const productionUrl = `https://images.buffer.com/dynamic-internal/fit-in/${MAX_IMAGE_WORKSPACE_SOURCE_DIMENSION}x${MAX_IMAGE_WORKSPACE_SOURCE_DIMENSION}/${canonicalUrl}`

  if (environment.isWithProd) return productionUrl
  if (environment.isWithStaging) {
    return productionUrl.replace('images.buffer.com', 'images.dev.buffer.com')
  }

  return getUrl(productionUrl)
}

export const getImageWorkspaceSourceDescriptor = (
  image: Image,
  environment: ImageWorkspaceSourceEnvironment = {
    isWithProd: IS_WITH_PROD,
    isWithStaging: IS_WITH_STAGING,
  },
): ImageWorkspaceSourceDescriptor => {
  const {
    url: canonicalUrl,
    width: canonicalWidth,
    height: canonicalHeight,
  } = image
  const hasSafeStoredDimensions =
    canonicalWidth !== null &&
    Number.isFinite(canonicalWidth) &&
    canonicalWidth > 0 &&
    canonicalHeight !== null &&
    Number.isFinite(canonicalHeight) &&
    canonicalHeight > 0
  const usesPreparationProxy =
    !hasSafeStoredDimensions ||
    (canonicalWidth ?? 0) > MAX_IMAGE_WORKSPACE_SOURCE_DIMENSION ||
    (canonicalHeight ?? 0) > MAX_IMAGE_WORKSPACE_SOURCE_DIMENSION

  const preparationUrl = getImageWorkspacePreparationUrl(
    canonicalUrl,
    environment,
  )

  return {
    requestUrl: usesPreparationProxy ? preparationUrl : canonicalUrl,
    preparationUrl,
    canonicalUrl,
    canonicalWidth,
    canonicalHeight,
    maxPreparedWidth: MAX_IMAGE_WORKSPACE_SOURCE_DIMENSION,
    maxPreparedHeight: MAX_IMAGE_WORKSPACE_SOURCE_DIMENSION,
    usesPreparationProxy,
  }
}
